import React, { useEffect, useState } from 'react';
import QRCode from 'react-qr-code';
import OiButton from '../../../../../shared/components/Common/OiButton/OiButton';
import OiFormInput from '../../../../../shared/components/OiForm/OiFormInput';
import MessageModal from '../../../../../shared/components/MessageModal/MessageModal';
import { userService } from '../../../../../modules/users/services/dependency.injection';

const STEP_STATUS = 'status';
const STEP_SCAN = 'scan';
const STEP_VERIFY = 'verify';

const TwoFactorAuth = () => {
    const [is2faEnabled, setIs2faEnabled] = useState(false);
    const [step, setStep] = useState(STEP_STATUS);
    const [qrCodeUrl, setQrCodeUrl] = useState('');
    const [secret, setSecret] = useState('');
    const [code, setCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [statusLoading, setStatusLoading] = useState(true);
    const [message, setMessage] = useState({ type: '', text: '' });

    useEffect(() => {
        fetchStatus();
    }, []);

    const fetchStatus = async () => {
        setStatusLoading(true);
        const response = await userService.get2FAStatusService();
        setStatusLoading(false);
        if (!response.isLeft()) {
            setIs2faEnabled(response.value.getValue().is2faEnabled);
        }
    };

    const handleStartSetup = async () => {
        setLoading(true);
        const response = await userService.setup2FAService();
        setLoading(false);
        if (response.isLeft()) {
            setMessage({ type: 'error', text: response.value });
        } else {
            const data = response.value.getValue();
            setQrCodeUrl(data.qrCodeUrl);
            setSecret(data.secret);
            setStep(STEP_SCAN);
        }
    };

    const handleVerify = async (event) => {
        event.preventDefault();
        setLoading(true);
        const response = await userService.enable2FAService(code);
        setLoading(false);
        if (response.isLeft()) {
            setMessage({ type: 'error', text: response.value });
        } else {
            setIs2faEnabled(true);
            setStep(STEP_STATUS);
            setCode('');
            setMessage({ type: 'success', text: '2FA successfully enabled!' });
        }
    };

    const handleDisable = async () => {
        setLoading(true);
        const response = await userService.disable2FAService();
        setLoading(false);
        if (response.isLeft()) {
            setMessage({ type: 'error', text: response.value });
        } else {
            setIs2faEnabled(false);
            setMessage({ type: 'success', text: '2FA successfully disabled.' });
        }
    };

    const clearMessage = () => setMessage({ type: '', text: '' });

    if (statusLoading) {
        return (
            <div className="content-background">
                <div className="content-background__wrapper">
                    <h2 className="settings__header u-half-gradient-bg">Security</h2>
                    <div className="oi-settings__data">
                        <p>Loading...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="content-background">
            <div className="content-background__wrapper">
                <h2 className="settings__header u-half-gradient-bg">Security</h2>

                <div className="oi-settings__data">
                    {!!message.text && (
                        <MessageModal
                            title=""
                            type={message.type}
                            message={message.text}
                            close={clearMessage}
                        />
                    )}

                    <div className="u-margin-top-3_5">
                        <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>
                            Two-Factor Authentication (2FA)
                        </h3>
                        <p style={{ fontSize: 13, color: '#666', marginBottom: 20 }}>
                            Add an extra layer of security to your account using an authenticator app
                            (Google Authenticator, Authy, etc.).
                        </p>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
                            <span style={{ fontSize: 13, fontWeight: 500 }}>Status:</span>
                            <span
                                style={{
                                    display: 'inline-block',
                                    padding: '2px 10px',
                                    borderRadius: 12,
                                    fontSize: 12,
                                    fontWeight: 600,
                                    background: is2faEnabled ? '#e6f9f0' : '#fef3e2',
                                    color: is2faEnabled ? '#1a8a4a' : '#b86e00'
                                }}
                            >
                                {is2faEnabled ? 'Enabled' : 'Disabled'}
                            </span>
                        </div>

                        {step === STEP_STATUS && (
                            <>
                                {!is2faEnabled ? (
                                    <div style={{ maxWidth: 260 }}>
                                        <OiButton
                                            disabled={loading}
                                            buttonLoader={loading}
                                            styles="oi-btn oi-btn--primary oi-btn--stretch oi-btn--radius5 oi-btn--height45 | u-font14sb"
                                            type="button"
                                            onClick={handleStartSetup}
                                        >
                                            Set up 2FA
                                        </OiButton>
                                    </div>
                                ) : (
                                    <div style={{ maxWidth: 260 }}>
                                        <OiButton
                                            disabled={loading}
                                            buttonLoader={loading}
                                            styles="oi-btn oi-btn--danger oi-btn--stretch oi-btn--radius5 oi-btn--height45 | u-font14sb"
                                            type="button"
                                            onClick={handleDisable}
                                        >
                                            Disable 2FA
                                        </OiButton>
                                    </div>
                                )}
                            </>
                        )}

                        {step === STEP_SCAN && (
                            <div>
                                <p style={{ fontSize: 13, marginBottom: 16 }}>
                                    Scan this QR code with your authenticator app, then enter the
                                    6-digit code to confirm.
                                </p>

                                <div style={{ background: '#fff', display: 'inline-block', padding: 16, borderRadius: 8, border: '1px solid #e5e7eb', marginBottom: 20 }}>
                                    <QRCode value={qrCodeUrl} size={180} />
                                </div>

                                <p style={{ fontSize: 12, color: '#888', marginBottom: 16 }}>
                                    Can't scan? Enter this key manually:{' '}
                                    <strong style={{ letterSpacing: 2, userSelect: 'all' }}>{secret}</strong>
                                </p>

                                <form onSubmit={handleVerify} style={{ maxWidth: 320 }}>
                                    <div className="oi-form__group">
                                        <OiFormInput
                                            labelClass="oi-form__label-primary"
                                            inputClass="oi-form__input-primary oi-form__input--height45"
                                            name="code"
                                            type="text"
                                            label="Verification Code"
                                            inputValue={code}
                                            onChange={e => setCode(e.target.value)}
                                            placeholder="Enter 6-digit code"
                                            required
                                        />
                                    </div>
                                    <div style={{ display: 'flex', gap: 12 }}>
                                        <OiButton
                                            disabled={loading || code.length !== 6}
                                            buttonLoader={loading}
                                            styles="oi-btn oi-btn--primary oi-btn--radius5 oi-btn--height45 | u-font14sb"
                                            type="submit"
                                        >
                                            Verify & Enable
                                        </OiButton>
                                        <OiButton
                                            disabled={loading}
                                            styles="oi-btn oi-btn--secondary oi-btn--radius5 oi-btn--height45 | u-font14sb"
                                            type="button"
                                            onClick={() => { setStep(STEP_STATUS); setCode(''); }}
                                        >
                                            Cancel
                                        </OiButton>
                                    </div>
                                </form>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TwoFactorAuth;
